import { baseApi, providesList, setAccessToken } from '@/shared/api'
import type {
  AcceptInvitationRequest,
  Invitation,
  LegalEntityList,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  Session,
} from '../model/types'

/** Приглашения больше нет: не найдено, использовано или истекло (P-AUTH-06#R-01, R-06). */
const INVITATION_GONE = new Set([404, 409, 410])

export const sessionApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Текущий сеанс: сотрудник, активное юрлицо и права.
     *
     * Запрос один на приложение — его делают ворота сеанса в слое app, остальных подписчиков
     * дедуплицирует RTK Query. `401` после неудачного обновления токена значит «сеанса нет»:
     * ворота ведут на вход.
     */
    getSession: build.query<Session, void>({
      query: () => ({ url: '/auth/me' }),
      providesTags: [{ type: 'Session', id: 'CURRENT' }],
    }),

    /**
     * Вход. Токен кладётся в память новым поколением сеанса, кеш сбрасывается целиком: данные
     * прошлого сеанса (и прошлого пользователя) не должны пережить вход. Ворота сеанса
     * перечитывают `/auth/me` сами — сброс переподписывает их запрос.
     */
    login: build.mutation<LoginResponse, LoginRequest>({
      query: (body) => ({ url: '/auth/login', method: 'POST', body }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          setAccessToken(data.accessToken)
          dispatch(baseApi.util.resetApiState())
        } catch {
          // Отказ разбирает форма входа по коду ошибки
        }
      },
    }),

    /**
     * Выход — только запрос. Токен и кеш чистит `features/auth-logout`, и только при `204` или
     * `401`: при сбое сети или `5xx` cookie на бэке не снята, и пользователь остаётся на месте
     * (P-AUTH-02#R-10). Сброс идёт одним тактом с переходом на вход, поэтому живёт рядом
     * с навигацией.
     */
    logout: build.mutation<void, void>({
      query: () => ({ url: '/auth/logout', method: 'POST' }),
    }),

    /**
     * Юрлица, доступные логину. Перечень малый и приходит целиком в конверте списка:
     * параметров страницы у ручки нет (`BE-AUTH-11`). Ключ строки — УНП.
     */
    listLegalEntities: build.query<LegalEntityList, void>({
      query: () => ({ url: '/auth/legal-entities' }),
      providesTags: (result) =>
        providesList(
          result?.items.map((item) => ({ id: item.unp })),
          'Company',
        ),
    }),

    /**
     * Смена юрлица. Ответ — сеанс в форме `/auth/me` уже с активным юрлицом: меняются цены,
     * состав заказа и права; токен прежний.
     *
     * Кеш прежнего юрлица сбрасывает вызывающий, одним тактом с переходом, и тем же тактом
     * кладёт этот ответ в `getSession` (`features/company-select`). Сброс здесь, до перехода,
     * успел бы перерисовать экран выбора пустым списком.
     */
    activateLegalEntity: build.mutation<Session, string>({
      query: (unp) => ({
        url: `/auth/legal-entities/${encodeURIComponent(unp)}/activate`,
        method: 'POST',
      }),
    }),

    /**
     * Регистрация по коду клиента. Учётка создаётся неактивной (`active=0`): ответ `204`, без
     * сеанса и без автовхода — войти можно после активации менеджером в 1С. Кеш не трогаем:
     * ничего отображаемого эта заявка не меняет. Отказ разбирает форма по коду:
     * `409 LOGIN_TAKEN` (под поле логина), `422` (поля), `429`/`503` (общая плашка).
     */
    register: build.mutation<void, RegisterRequest>({
      query: (body) => ({ url: '/auth/register', method: 'POST', body }),
    }),

    getInvitation: build.query<Invitation, string>({
      query: (token) => ({ url: `/auth/invitations/${encodeURIComponent(token)}` }),
      providesTags: (_result, _error, token) => [{ type: 'Invitation', id: token }],
    }),

    /**
     * Установка пароля. Успех и «приглашения больше нет» (`404`, `409`, `410`) делают его кеш
     * устаревшим: приглашение перечитывается и показывает причину вместо формы. Сбой сети
     * и ошибка поля кеш не трогают — форма остаётся с введённым.
     */
    acceptInvitation: build.mutation<void, AcceptInvitationRequest>({
      query: ({ token, password }) => ({
        url: `/auth/invitations/${encodeURIComponent(token)}/accept`,
        method: 'POST',
        body: { password },
      }),
      invalidatesTags: (_result, error, { token }) =>
        error === undefined ||
        (typeof error.status === 'number' && INVITATION_GONE.has(error.status))
          ? [{ type: 'Invitation', id: token }]
          : [],
    }),
  }),
})

export const {
  useGetSessionQuery,
  useLoginMutation,
  useLogoutMutation,
  useListLegalEntitiesQuery,
  useActivateLegalEntityMutation,
  useRegisterMutation,
  useGetInvitationQuery,
  useAcceptInvitationMutation,
} = sessionApi
